import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ALL_SLOTS, isInSeason, isDayOpen, MAX_PLAYERS_PER_SLOT, getFormulaById, isSlotTooSoon } from '../config/formulas';
import '../styles/DailyView.css';

function DailyView({ 
  currentDate, 
  sessions, 
  bookings, 
  isAdmin,
  onDeleteBooking, 
  onUpdateBookingPayment,
  onUpdateBookingNotes,
  onBlockSession,
  onUnblockSession,
  onCreateSession,
  onOpenSlot,
  onCloseSlot,
  onOpenDay,
  onCloseDay,
  fieldMode = false,
  fieldModeUntil = null,
  onToggleFieldMode
}) {
  const { dateParam } = useParams();
  const navigate = useNavigate();
  
  // États UI
  const [deletingBookingId, setDeletingBookingId] = useState(null);
  const [editingNotesId, setEditingNotesId] = useState(null);
  const [tempNotes, setTempNotes] = useState('');
  const [showBookingDetail, setShowBookingDetail] = useState(null);
  const [showSlotAdmin, setShowSlotAdmin] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [isBlockingMode, setIsBlockingMode] = useState(false);
  
  // Date du jour affiché
  const date = dateParam ? new Date(dateParam + 'T12:00:00') : currentDate;
  const dateStr = dateParam || `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  
  // Sessions du jour
  const daySessions = useMemo(() => 
    sessions.filter(session => session.date === dateStr),
    [sessions, dateStr]
  );
  
  // Réservations du jour (exclure les annulées)
  const dayBookings = useMemo(() => 
    bookings.filter(b => b.date === dateStr && b.status !== 'cancelled'),
    [bookings, dateStr]
  );

  // Jour en saison ?
  const inSeason = isInSeason(dateStr);
  // Jour ouvert (au moins un créneau ouvert) ?
  const isOpen = isDayOpen(dateStr, sessions);
  // Combien de créneaux ouverts ?
  const openSlotsCount = daySessions.filter(s => s.isOpen === true).length;

  // Réservations par créneau
  const getSlotBookings = (slotTime) => {
    return dayBookings.filter(b => b.slots && b.slots.includes(slotTime));
  };

  // Navigation jour
  const navigateDay = (delta) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    navigate(`/daily/${year}-${month}-${day}`);
  };

  // ====== ACTIONS ADMIN ======

  // Ouvrir tous les créneaux du jour
  const handleOpenDay = async () => {
    try {
      await onOpenDay(dateStr);
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  // Fermer tous les créneaux du jour
  const handleCloseDay = async () => {
    try {
      await onCloseDay(dateStr);
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  // Ouvrir/fermer un créneau individuel
  const handleToggleSlot = async (session, shouldOpen) => {
    try {
      if (shouldOpen) {
        await onOpenSlot(session.id);
      } else {
        await onCloseSlot(session.id);
      }
      setShowSlotAdmin(null);
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  // Bloquer un créneau
  const handleBlock = async (session) => {
    if (!blockReason.trim()) {
      alert("Veuillez indiquer une raison pour le blocage.");
      return;
    }
    try {
      await onBlockSession(session.id, blockReason);
      setShowSlotAdmin(null);
      setBlockReason('');
      setIsBlockingMode(false);
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  // Débloquer un créneau
  const handleUnblock = async (session) => {
    try {
      await onUnblockSession(session.id);
      setShowSlotAdmin(null);
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  // Clic sur un créneau → ouvrir le panneau admin
  const handleSlotClick = async (time) => {
    let session = daySessions.find(s => s.time === time);
    
    // Si la session n'existe pas encore dans Firestore, la créer
    if (!session && onCreateSession) {
      try {
        session = await onCreateSession({
          date: dateStr,
          time,
          isBlocked: false,
          blockReason: '',
          participantCount: 0,
          paidCount: 0
        });
      } catch (err) {
        alert("Erreur lors de la création du créneau : " + err.message);
        return;
      }
    }
    
    if (session) {
      setShowSlotAdmin(session);
      setIsBlockingMode(false);
      setBlockReason('');
    }
  };

  // Supprimer une réservation
  const handleDeleteBooking = async (bookingId) => {
    try {
      await onDeleteBooking(bookingId);
      setDeletingBookingId(null);
      setShowBookingDetail(null);
    } catch (err) {
      alert("Erreur lors de la suppression : " + err.message);
    }
  };

  // Basculer le paiement
  const handleTogglePayment = async (booking) => {
    try {
      await onUpdateBookingPayment(booking.id, !booking.isPaid);
      setShowBookingDetail({ ...booking, isPaid: !booking.isPaid });
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };
  
  return (
    <div className="daily-view-container">
      {/* En-tête */}
      <div className="daily-view-header">
        <button onClick={() => navigate('/')} className="back-button">
          &lt; Calendrier
        </button>
        <h2>{date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h2>
        <div className="date-navigation">
          <button onClick={() => navigateDay(-1)} className="date-nav-button">
            &lt; Veille
          </button>
          <button onClick={() => navigateDay(1)} className="date-nav-button">
            Lendemain &gt;
          </button>
        </div>
      </div>
      
      {inSeason ? (
        <>
          {/* Barre d'actions admin journée (seulement admin) */}
          {isAdmin && (
            <div className="day-admin-bar">
              <div className="day-status">
                {isOpen ? (
                  <span className="day-status-open">✓ Journée ouverte ({openSlotsCount} créneau(x))</span>
                ) : (
                  <span className="day-status-closed">Journée fermée</span>
                )}
              </div>
              <div className="day-admin-buttons">
                <button onClick={handleOpenDay} className="open-day-button">
                  ☀️ Ouvrir tous les créneaux
                </button>
                {isOpen && (
                  <button onClick={handleCloseDay} className="close-day-button">
                    🌙 Fermer tous les créneaux
                  </button>
                )}
                {/* Mode terrain : lève le délai d'1h pour tous les clients */}
                {onToggleFieldMode && (
                  <button
                    onClick={() => {
                      if (fieldMode) {
                        onToggleFieldMode(false);
                      } else if (window.confirm(
                        "Activer le MODE TERRAIN ?\n\n" +
                        "Vos clients pourront réserver un créneau démarrant dans moins d'une heure.\n" +
                        "À n'activer que si vous êtes sur place, matériel installé.\n\n" +
                        "Le mode se désactivera automatiquement à minuit."
                      )) {
                        onToggleFieldMode(true);
                      }
                    }}
                    className={fieldMode ? 'field-mode-button active' : 'field-mode-button'}
                  >
                    {fieldMode ? '🎯 Mode terrain : ACTIF' : '🎯 Mode terrain : inactif'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Bandeau bien visible quand le mode terrain est actif */}
          {fieldMode && (
            <div className="field-mode-banner">
              <span className="field-mode-banner-icon">🎯</span>
              <div className="field-mode-banner-text">
                <strong>Mode terrain actif</strong>
                <span>
                  {isAdmin
                    ? `Les clients peuvent réserver sans délai${fieldModeUntil ? ` — extinction automatique à minuit` : ''}.`
                    : "Réservation de dernière minute possible : nous sommes sur place !"}
                </span>
              </div>
              {isAdmin && onToggleFieldMode && (
                <button
                  onClick={() => onToggleFieldMode(false)}
                  className="field-mode-banner-off"
                >
                  Désactiver
                </button>
              )}
            </div>
          )}

          {/* Bouton nouvelle réservation (seulement si jour ouvert) */}
          {isOpen && (
            <div className="new-booking-bar">
              <button 
                onClick={() => navigate(`/booking/${dateStr}`)}
                className="new-booking-button"
              >
                + Nouvelle réservation
              </button>
              {isAdmin && dayBookings.length > 0 && (
                <div className="day-summary">
                  <span className="summary-bookings">{dayBookings.length} réservation(s)</span>
                  <span className="summary-sep">·</span>
                  <span className="summary-participants">
                    {dayBookings.reduce((sum, b) => sum + (b.nbParticipants || 0), 0)} joueur(s)
                  </span>
                  <span className="summary-sep">·</span>
                  <span className="summary-revenue">
                    {dayBookings.reduce((sum, b) => sum + (b.totalPrice || 0), 0)}€
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Timeline des créneaux */}
          <div className="sessions-list">
            <div className="sessions-timeline">
              {ALL_SLOTS.map((time, index) => {
                const session = daySessions.find(s => s.time === time);
                const slotBookings = getSlotBookings(time);
                const isBlocked = session && session.isBlocked;
                const isSlotOpen = session && session.isOpen === true;
                const playerCount = session ? (session.participantCount || 0) : 0;
                const isFull = playerCount >= MAX_PLAYERS_PER_SLOT;
                
                // Public : créneau trop proche dans le temps (moins d'1h avant le début)
                // Le mode terrain lève cette restriction pour tout le monde.
                const tooSoon = !isAdmin && !fieldMode && isSlotTooSoon(dateStr, time);
                // Public : le créneau est-il cliquable pour lancer une réservation ?
                const canPublicBook = !isAdmin && isSlotOpen && !isBlocked && !isFull && !tooSoon;
                
                // Pause déjeuner
                const prevSlot = index > 0 ? ALL_SLOTS[index - 1] : null;
                const showLunchBreak = prevSlot === '11:30' && time === '14:00';
                
                return (
                  <React.Fragment key={time}>
                    {showLunchBreak && (isAdmin || isSlotOpen) && (
                      <div className="lunch-break">
                        <span>🍽️ Pause déjeuner (12h - 14h)</span>
                      </div>
                    )}
                    
                    {/* Le public ne voit que les créneaux ouverts */}
                    {(isAdmin || isSlotOpen) && (
                    <div
                      className={`timeline-slot ${
                      isBlocked ? 'blocked' : 
                      !isSlotOpen ? 'closed' :
                      isFull ? 'full' : 
                      slotBookings.length > 0 ? 'has-bookings' : 'available'
                    } ${canPublicBook ? 'public-clickable' : ''} ${tooSoon && isSlotOpen && !isBlocked ? 'too-soon' : ''}`}
                      onClick={canPublicBook ? () => navigate(`/booking/${dateStr}`) : undefined}
                      title={canPublicBook ? `Réserver le créneau de ${time}` : ''}
                      role={canPublicBook ? 'button' : undefined}
                      tabIndex={canPublicBook ? 0 : undefined}
                      onKeyDown={canPublicBook ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/booking/${dateStr}`);
                        }
                      } : undefined}
                    >
                      {/* Colonne heure + capacité */}
                      <div 
                        className="slot-time-col"
                        onClick={() => isAdmin ? handleSlotClick(time) : null}
                        title={isAdmin ? "Cliquer pour gérer ce créneau" : ""}
                        style={{ cursor: isAdmin ? 'pointer' : 'default' }}
                      >
                        <span className="slot-time-label">{time}</span>
                        <span className="slot-capacity">
                          {isBlocked ? '🔒' : !isSlotOpen ? '—' : `${playerCount}/${MAX_PLAYERS_PER_SLOT}`}
                        </span>
                        {isAdmin && <span className="slot-admin-icon">⚙️</span>}
                      </div>
                      
                      {/* Colonne contenu */}
                      <div className="slot-content-col">
                        {isBlocked ? (
                          <div className="slot-blocked-info" onClick={() => isAdmin ? handleSlotClick(time) : null}>
                            <span className="blocked-badge">Bloqué</span>
                            {session.blockReason && (
                              <span className="blocked-reason">{session.blockReason}</span>
                            )}
                            {isAdmin && <span className="click-hint">Cliquer pour débloquer</span>}
                          </div>
                        ) : !isSlotOpen ? (
                          <div className="slot-closed" onClick={() => isAdmin ? handleSlotClick(time) : null}>
                            <span className="closed-label">Fermé</span>
                            {isAdmin && <span className="click-hint">Cliquer pour ouvrir</span>}
                          </div>
                        ) : slotBookings.length > 0 ? (
                          <div className="slot-bookings">
                            {isAdmin ? (
                              // VUE ADMIN : détails complets, cliquable
                              slotBookings.map(booking => {
                                const formula = getFormulaById(booking.formulaId);
                                const isFirstSlot = booking.startTime === time;
                                
                                return (
                                  <div 
                                    key={booking.id}
                                    className={`booking-chip ${isFirstSlot ? 'first-slot' : 'continuation'}`}
                                    style={{ 
                                      borderLeftColor: formula?.color || '#94a3b8',
                                      backgroundColor: isFirstSlot ? `${formula?.color}10` : `${formula?.color}05`
                                    }}
                                    onClick={() => setShowBookingDetail(booking)}
                                  >
                                    {isFirstSlot ? (
                                      <>
                                        <span className="chip-icon">{formula?.icon || '📋'}</span>
                                        <span className="chip-name">{booking.contactName}</span>
                                        <span className="chip-formula">{booking.formulaName}</span>
                                        <span className="chip-players">{booking.nbParticipants}p</span>
                                        <span className={`chip-payment ${booking.isPaid ? 'paid' : 'unpaid'}`}>
                                          {booking.isPaid ? '✓' : '€'}
                                        </span>
                                      </>
                                    ) : (
                                      <span className="chip-continuation">↳ {booking.contactName}</span>
                                    )}
                                  </div>
                                );
                              })
                            ) : (
                              // VUE PUBLIC : juste "Réservé" avec le nombre de places restantes
                              <div className="slot-public-info">
                                <span className="public-reserved">Réservé</span>
                                <span className="public-remaining">
                                  {MAX_PLAYERS_PER_SLOT - playerCount > 0 
                                    ? `${MAX_PLAYERS_PER_SLOT - playerCount} place(s) restante(s)` 
                                    : 'Complet'}
                                </span>
                                {canPublicBook && (
                                  <span className="book-cta">Réserver →</span>
                                )}
                                {tooSoon && !isFull && (
                                  <span className="too-late-hint">Réservation en ligne fermée</span>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="slot-empty" onClick={() => isAdmin ? handleSlotClick(time) : null}>
                            <span className="empty-label">Disponible</span>
                            {isAdmin && <span className="click-hint">Cliquer pour gérer</span>}
                            {canPublicBook && (
                              <span className="book-cta">Réserver →</span>
                            )}
                            {tooSoon && (
                              <span className="too-late-hint">Réservation en ligne fermée</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <div className="off-season-message">
          <p>Ce jour est hors saison (avril à octobre).</p>
          <button onClick={() => navigate('/')} className="calendar-button">
            Retour au calendrier
          </button>
        </div>
      )}

      {/* ====== MODAL ADMIN CRÉNEAU ====== */}
      {showSlotAdmin && (
        <div className="modal-overlay" onClick={() => { setShowSlotAdmin(null); setIsBlockingMode(false); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚙️ Créneau {showSlotAdmin.time}</h3>
              <button className="modal-close" onClick={() => { setShowSlotAdmin(null); setIsBlockingMode(false); }}>✕</button>
            </div>

            <div className="slot-admin-info">
              <div className="detail-row">
                <span className="detail-label">Date</span>
                <span className="detail-value">{date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Statut</span>
                <span className={`detail-value ${showSlotAdmin.isBlocked ? 'detail-unpaid' : 'detail-paid'}`}>
                  {showSlotAdmin.isBlocked ? '🔒 Bloqué' : '✓ Ouvert'}
                </span>
              </div>
              {!showSlotAdmin.isBlocked && (
                <div className="detail-row">
                  <span className="detail-label">Joueurs</span>
                  <span className="detail-value">{showSlotAdmin.participantCount || 0} / {MAX_PLAYERS_PER_SLOT}</span>
                </div>
              )}
              {showSlotAdmin.isBlocked && showSlotAdmin.blockReason && (
                <div className="detail-row">
                  <span className="detail-label">Raison</span>
                  <span className="detail-value">{showSlotAdmin.blockReason}</span>
                </div>
              )}
            </div>

            {/* Réservations sur ce créneau */}
            {!showSlotAdmin.isBlocked && getSlotBookings(showSlotAdmin.time).length > 0 && (
              <div className="slot-admin-bookings">
                <h4>Réservations sur ce créneau :</h4>
                {getSlotBookings(showSlotAdmin.time).map(booking => {
                  const formula = getFormulaById(booking.formulaId);
                  return (
                    <div key={booking.id} className="admin-booking-row" onClick={() => { setShowSlotAdmin(null); setShowBookingDetail(booking); }}>
                      <span className="admin-booking-icon">{formula?.icon || '📋'}</span>
                      <span className="admin-booking-name">{booking.contactName}</span>
                      <span className="admin-booking-formula">{booking.formulaName}</span>
                      <span className="admin-booking-players">{booking.nbParticipants}p</span>
                      <span className={`admin-booking-paid ${booking.isPaid ? 'paid' : 'unpaid'}`}>
                        {booking.isPaid ? 'Payé' : 'Non payé'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Actions admin */}
            <div className="slot-admin-actions">
              {/* Toggle ouvert/fermé */}
              {showSlotAdmin.isOpen ? (
                <button className="close-slot-button" onClick={() => handleToggleSlot(showSlotAdmin, false)}>
                  🌙 Fermer ce créneau
                </button>
              ) : (
                <button className="open-slot-button" onClick={() => handleToggleSlot(showSlotAdmin, true)}>
                  ☀️ Ouvrir ce créneau
                </button>
              )}

              {/* Blocage (seulement si ouvert et non bloqué) */}
              {showSlotAdmin.isOpen && !showSlotAdmin.isBlocked && (
                <>
                  {!isBlockingMode ? (
                    <button className="block-button" onClick={() => setIsBlockingMode(true)}>
                      🔒 Bloquer ce créneau
                    </button>
                  ) : (
                    <div className="block-form">
                      <label>Raison du blocage :</label>
                      <input
                        type="text"
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        placeholder="Ex: Réservé groupe école"
                        className="block-reason-input"
                        autoFocus
                      />
                      <div className="block-form-actions">
                        <button className="confirm-block-btn" onClick={() => handleBlock(showSlotAdmin)}>
                          Confirmer le blocage
                        </button>
                        <button className="cancel-block-btn" onClick={() => { setIsBlockingMode(false); setBlockReason(''); }}>
                          Annuler
                        </button>
                      </div>
                      {(showSlotAdmin.participantCount || 0) > 0 && (
                        <p className="block-warning">
                          ⚠️ Attention : le blocage supprimera les réservations existantes sur ce créneau.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Déblocage (seulement si bloqué) */}
              {showSlotAdmin.isBlocked && (
                <button className="unblock-button" onClick={() => handleUnblock(showSlotAdmin)}>
                  🔓 Débloquer ce créneau
                </button>
              )}

              {/* Nouvelle réservation (seulement si ouvert et non bloqué) */}
              {showSlotAdmin.isOpen && !showSlotAdmin.isBlocked && (
                <button 
                  className="new-booking-from-slot-btn"
                  onClick={() => { setShowSlotAdmin(null); navigate(`/booking/${dateStr}`); }}
                >
                  + Nouvelle réservation
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ====== MODAL DÉTAIL RÉSERVATION ====== */}
      {showBookingDetail && (
        <div className="modal-overlay" onClick={() => setShowBookingDetail(null)}>
          <div className="modal-content booking-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {getFormulaById(showBookingDetail.formulaId)?.icon}{' '}
                Réservation — {showBookingDetail.formulaName}
              </h3>
              <button className="modal-close" onClick={() => setShowBookingDetail(null)}>✕</button>
            </div>
            
            <div className="detail-rows">
              <div className="detail-row">
                <span className="detail-label">Contact</span>
                <span className="detail-value">{showBookingDetail.contactName}</span>
              </div>
              {showBookingDetail.contactPhone && (
                <div className="detail-row">
                  <span className="detail-label">Téléphone</span>
                  <span className="detail-value">{showBookingDetail.contactPhone}</span>
                </div>
              )}
              {showBookingDetail.contactEmail && (
                <div className="detail-row">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{showBookingDetail.contactEmail}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Horaires</span>
                <span className="detail-value">
                  {showBookingDetail.slots[0]} → {showBookingDetail.slots[showBookingDetail.slots.length - 1]}
                  ({showBookingDetail.slots.length} créneau(x))
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Participants</span>
                <span className="detail-value">{showBookingDetail.nbParticipants} personne(s)</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Prix</span>
                <span className="detail-value detail-price">{showBookingDetail.totalPrice}€</span>
              </div>

              {/* Détail des options choisies */}
              {showBookingDetail.selectedOptions && showBookingDetail.selectedOptions.length > 0 && (
                <div className="detail-row detail-options-row">
                  <span className="detail-label">Options</span>
                  <div className="detail-options-list">
                    {showBookingDetail.selectedOptions.map(opt => (
                      <div key={opt.id} className="detail-option-item">
                        <span className="detail-option-name">
                          {opt.name}
                          {opt.priceType === 'per_unit' && opt.quantity > 1 && ` ×${opt.quantity}`}
                        </span>
                        <span className="detail-option-price">+{opt.subtotal}€</span>
                      </div>
                    ))}
                    {showBookingDetail.optionsPrice > 0 && (
                      <div className="detail-option-total">
                        Total options : +{showBookingDetail.optionsPrice}€
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="detail-row">
                <span className="detail-label">Paiement</span>
                <span className={`detail-value ${showBookingDetail.isPaid ? 'detail-paid' : 'detail-unpaid'}`}>
                  {showBookingDetail.isPaid ? '✓ Payé' : '✗ Non payé'}
                </span>
              </div>
              {showBookingDetail.notes && (
                <div className="detail-row">
                  <span className="detail-label">Notes client</span>
                  <span className="detail-value detail-notes">{showBookingDetail.notes}</span>
                </div>
              )}

              {/* Notes admin */}
              {isAdmin && (
                <div className="detail-row admin-notes-row">
                  <span className="detail-label">📝 Notes admin</span>
                  {editingNotesId === showBookingDetail.id ? (
                    <div className="admin-notes-edit">
                      <textarea
                        value={tempNotes}
                        onChange={(e) => setTempNotes(e.target.value)}
                        placeholder="Notes privées (ex: groupe scolaire, contact directrice...)"
                        rows={3}
                        className="admin-notes-textarea"
                      />
                      <div className="admin-notes-actions">
                        <button 
                          className="notes-save-btn"
                          onClick={async () => {
                            if (onUpdateBookingNotes) {
                              await onUpdateBookingNotes(showBookingDetail.id, tempNotes);
                              showBookingDetail.adminNotes = tempNotes;
                            }
                            setEditingNotesId(null);
                          }}
                        >
                          Enregistrer
                        </button>
                        <button 
                          className="notes-cancel-btn"
                          onClick={() => setEditingNotesId(null)}
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="admin-notes-display" onClick={() => {
                      setEditingNotesId(showBookingDetail.id);
                      setTempNotes(showBookingDetail.adminNotes || '');
                    }}>
                      {showBookingDetail.adminNotes || <em style={{color:'#94a3b8'}}>Cliquez pour ajouter une note...</em>}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button 
                className={`toggle-payment-btn ${showBookingDetail.isPaid ? 'mark-unpaid' : 'mark-paid'}`}
                onClick={() => handleTogglePayment(showBookingDetail)}
              >
                {showBookingDetail.isPaid ? 'Marquer non payé' : '✓ Marquer payé'}
              </button>
              
              {deletingBookingId === showBookingDetail.id ? (
                <div className="confirm-delete">
                  <span>Confirmer la suppression ?</span>
                  <button className="confirm-yes" onClick={() => handleDeleteBooking(showBookingDetail.id)}>
                    Oui, supprimer
                  </button>
                  <button className="confirm-no" onClick={() => setDeletingBookingId(null)}>
                    Non
                  </button>
                </div>
              ) : (
                <button 
                  className="delete-booking-btn"
                  onClick={() => setDeletingBookingId(showBookingDetail.id)}
                >
                  Supprimer la réservation
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DailyView;
